import React from "react";
import {
  Plus,
  RefreshCw,
  CheckCircle2,
  XCircle,
  ArrowRightLeft,
  Sparkles,
  FileText,
  Trash2,
  Loader2,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useIngestionEvents } from "@/hooks/useIngestionEvents";
import { formatDistanceToNow } from "date-fns";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import type { IngestionEventType } from "./types";

interface IngestionEventTimelineProps {
  ingestionId: string | null;
}

const eventIcons: Record<IngestionEventType, React.ReactNode> = {
  created: <Plus className="w-3 h-3" />,
  scrape_started: <RefreshCw className="w-3 h-3" />,
  scrape_completed: <CheckCircle2 className="w-3 h-3" />,
  scrape_failed: <XCircle className="w-3 h-3" />,
  status_changed: <ArrowRightLeft className="w-3 h-3" />,
  data_type_changed: <ArrowRightLeft className="w-3 h-3" />,
  review_status_changed: <ArrowRightLeft className="w-3 h-3" />,
  extraction_started: <Sparkles className="w-3 h-3" />,
  extraction_completed: <Sparkles className="w-3 h-3" />,
  extraction_failed: <XCircle className="w-3 h-3" />,
  ai_notes_updated: <FileText className="w-3 h-3" />,
  content_updated: <FileText className="w-3 h-3" />,
  deleted: <Trash2 className="w-3 h-3" />,
};

const eventLabels: Record<IngestionEventType, string> = {
  created: "Created",
  scrape_started: "Scrape started",
  scrape_completed: "Scrape completed",
  scrape_failed: "Scrape failed",
  status_changed: "Status changed",
  data_type_changed: "Data type changed",
  review_status_changed: "Review status changed",
  extraction_started: "Extraction started",
  extraction_completed: "Extraction completed",
  extraction_failed: "Extraction failed",
  ai_notes_updated: "AI notes updated",
  content_updated: "Content updated",
  deleted: "Deleted",
};

const eventColors: Record<IngestionEventType, string> = {
  created: "text-blue-400 bg-blue-500/20",
  scrape_started: "text-purple-400 bg-purple-500/20",
  scrape_completed: "text-green-400 bg-green-500/20",
  scrape_failed: "text-red-400 bg-red-500/20",
  status_changed: "text-yellow-400 bg-yellow-500/20",
  data_type_changed: "text-orange-400 bg-orange-500/20",
  review_status_changed: "text-cyan-400 bg-cyan-500/20",
  extraction_started: "text-purple-400 bg-purple-500/20",
  extraction_completed: "text-emerald-400 bg-emerald-500/20",
  extraction_failed: "text-red-400 bg-red-500/20",
  ai_notes_updated: "text-violet-400 bg-violet-500/20",
  content_updated: "text-amber-400 bg-amber-500/20",
  deleted: "text-red-400 bg-red-500/20",
};

const IngestionEventTimeline: React.FC<IngestionEventTimelineProps> = ({
  ingestionId,
}) => {
  const [isOpen, setIsOpen] = React.useState(false);
  const { data: events, isLoading } = useIngestionEvents(ingestionId);

  if (!ingestionId) return null;

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="mt-4">
      <CollapsibleTrigger className="flex items-center gap-2 text-white/60 hover:text-white/80 text-sm transition-colors">
        {isOpen ? (
          <ChevronDown className="w-4 h-4" />
        ) : (
          <ChevronRight className="w-4 h-4" />
        )}
        Event Timeline
        {events && events.length > 0 && (
          <span className="text-white/40">({events.length})</span>
        )}
      </CollapsibleTrigger>

      <CollapsibleContent className="mt-2">
        {isLoading ? (
          <div className="flex items-center gap-2 text-white/40 text-sm py-2">
            <Loader2 className="w-3 h-3 animate-spin" />
            Loading events...
          </div>
        ) : !events || events.length === 0 ? (
          <div className="text-white/40 text-sm py-2">No events recorded</div>
        ) : (
          <div className="space-y-2 pl-2 border-l border-white/10">
            {events.map((event) => (
              <div key={event.id} className="flex items-start gap-2 pl-3 relative">
                {/* Timeline dot */}
                <div
                  className={`absolute -left-1.5 top-1 w-3 h-3 rounded-full flex items-center justify-center ${
                    eventColors[event.event_type as IngestionEventType] || "text-white/40 bg-white/10"
                  }`}
                >
                  {eventIcons[event.event_type as IngestionEventType] || (
                    <div className="w-1.5 h-1.5 rounded-full bg-current" />
                  )}
                </div>

                {/* Event content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-white/80 text-sm font-medium">
                      {eventLabels[event.event_type as IngestionEventType] ||
                        event.event_type}
                    </span>
                    <span className="text-white/30 text-xs">
                      {formatDistanceToNow(new Date(event.created_at), {
                        addSuffix: true,
                      })}
                    </span>
                  </div>

                  {/* Payload summary */}
                  {event.payload && Object.keys(event.payload).length > 0 && (
                    <div className="text-white/40 text-xs mt-0.5 font-mono truncate">
                      {event.event_type === "status_changed" ||
                      event.event_type === "data_type_changed" ||
                      event.event_type === "review_status_changed" ? (
                        <>
                          {String(event.payload.from || "null")} → {String(event.payload.to || "null")}
                        </>
                      ) : (
                        JSON.stringify(event.payload).slice(0, 80) +
                        (JSON.stringify(event.payload).length > 80 ? "..." : "")
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CollapsibleContent>
    </Collapsible>
  );
};

export default IngestionEventTimeline;
